import React, { useState, useEffect, useContext } from 'react';
import { collection, query, onSnapshot, doc, deleteDoc, getDoc } from 'firebase/firestore';
import { AuthContext } from '../App';

// Re-using MessageBox with updated styling to match App.js
function MessageBox({ message, onClose }) {
  if (!message) return null;
  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full text-center">
        <p className="text-lg font-semibold text-gray-800 mb-6">{message}</p>
        <button
          onClick={onClose}
          className="px-6 py-3 rounded-lg shadow-md text-white font-semibold transition-all duration-300 hover:shadow-lg"
          style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          OK
        </button>
      </div>
    </div>
  );
}

// Custom Confirmation Modal with updated styling
function ConfirmationModal({ message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full text-center">
        <p className="text-lg font-semibold text-gray-800 mb-6">{message}</p>
        <div className="flex justify-center space-x-4">
          <button
            onClick={onConfirm}
            className="px-6 py-3 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-300"
            style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            Confirm
          </button>
          <button
            onClick={onCancel}
            className="px-6 py-3 bg-gray-300 text-gray-800 rounded-lg font-semibold hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 transition duration-200 shadow-md"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BlockedUsersPage() {
  const { userId, db, isAuthReady, user } = useContext(AuthContext);
  const [blockedProfiles, setBlockedProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  // States for Confirmation Modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState('');
  const [confirmModalCallback, setConfirmModalCallback] = useState(() => {});
  

  useEffect(() => {
    let unsubscribeBlocked;

    if (isAuthReady && userId && db) {
      setLoading(true);
      setMessage('');

      // Reference to the current user's blockedUsers subcollection
      const blockedUsersRef = collection(db, `users/${userId}/blockedUsers`);
      const q = query(blockedUsersRef);

      unsubscribeBlocked = onSnapshot(q, async (snapshot) => {
        const blockedUserIds = snapshot.docs.map(doc => doc.id);

        // Fetch profile details for each blocked user
        const fetchedBlockedProfiles = await Promise.all(
          blockedUserIds.map(async (blockedId) => {
            try {
              const profileDocRef = doc(db, 'profiles', blockedId);
              const profileDocSnap = await getDoc(profileDocRef);

              if (profileDocSnap.exists()) {
                const profileData = profileDocSnap.data();
                return { id: blockedId, ...profileData };
              } else {
                const userDocRef = doc(db, 'users', blockedId);
                const userDocSnap = await getDoc(userDocRef);
                if (userDocSnap.exists()) {
                  return { id: blockedId, ...userDocSnap.data(), profileSummary: 'No detailed profile available' };
                }
              }
            } catch (error) {
              console.error(`Error fetching profile for blocked user ${blockedId}:`, error);
            }
            return null;
          })
        );

        setBlockedProfiles(fetchedBlockedProfiles.filter(p => p !== null));
        setLoading(false);

        if (blockedUserIds.length === 0) {
          setMessage('You have not blocked any users yet.');
        } else {
          setMessage('');
        }
      }, (error) => {
        console.error("Error fetching blocked users list:", error);
        setMessage(`Failed to load blocked users: ${error.message}`);
        setLoading(false);
      });
    } else if (isAuthReady && !userId) {
        setLoading(false);
        setMessage('Please log in to view your blocked users.');
    } else {
        setLoading(true);
    }

    return () => {
      if (unsubscribeBlocked) unsubscribeBlocked();
    };
  }, [userId, db, isAuthReady, user]);

  const handleUnblockUser = (profile) => {
    setConfirmModalMessage(`Are you sure you want to unblock ${profile.firstName}? They may reappear in your discover feed.`);
    setConfirmModalCallback(() => async () => {
      setLoading(true);
      try {
        const blockedDocRef = doc(db, `users/${userId}/blockedUsers`, profile.id);
        await deleteDoc(blockedDocRef);
        setMessage(`${profile.firstName} has been unblocked.`);
      } catch (error) {
        setMessage(`Failed to unblock ${profile.firstName}: ${error.message}`);
        console.error("Unblock error:", error);
      } finally {
        setLoading(false);
        setShowConfirmModal(false);
      }
    });
   
    setShowConfirmModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)' }}>
        <p className="text-xl text-white">Loading blocked users...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4" style={{ background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)' }}>
        <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full text-center">
          <p className="text-lg font-semibold text-gray-800 mb-4">Please log in to view your blocked users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 pt-32 pb-8">
      {message && <MessageBox message={message} onClose={() => setMessage('')} />}
      {showConfirmModal && (
        <ConfirmationModal
          message={confirmModalMessage}
          onConfirm={confirmModalCallback}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}

      {/* Header Section */}
      <div className="bg-white p-8 rounded-2xl shadow-xl mb-8 transform hover:shadow-2xl transition-all duration-300">
        <div className="text-center">
          <h2 className="text-4xl font-bold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
            Blocked Users
          </h2>
          <p className="text-gray-600 text-lg">Manage users you have blocked from contacting you</p>
        </div>
      </div>

      {blockedProfiles.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl shadow-xl text-center transform hover:shadow-2xl transition-all duration-300">
          <div className="w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center"
               style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <p className="text-2xl font-semibold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
            No Blocked Users
          </p>
          <p className="text-gray-600">You haven't blocked anyone yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {blockedProfiles.map((profile) => (
            <div key={profile.id} className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col transform hover:shadow-2xl hover:-translate-y-2 transition-all duration-300">
              <div className="relative h-56 w-full bg-gray-200 flex items-center justify-center overflow-hidden">
                <img
                  src={profile.profilePhotoUrl || "https://placehold.co/400x400/E0E7FF/4F46E5?text=No+Photo"}
                  alt={profile.firstName || "Profile"}
                  className="w-full h-full object-cover"
                  onError={(e) => { 
                    e.target.onerror = null; 
                    e.target.src = "https://placehold.co/400x400/E0E7FF/4F46E5?text=No+Photo"; 
                  }}
                />
                <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                  Blocked
                </div>
              </div>
              
              <div className="p-6 flex-grow flex flex-col">
                <h3 className="text-2xl font-bold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
                  {profile.firstName} {profile.lastName}
                </h3>
                
                {profile.dob && profile.dob.toDate && (
                  <p className="text-gray-600 text-sm mb-2 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {profile.gender}, {new Date().getFullYear() - profile.dob.toDate().getFullYear()} years
                  </p>
                )}
                
                <p className="text-gray-600 text-sm mb-4 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {profile.city}, {profile.state}
                </p>
                
                <p className="text-gray-700 text-sm mb-6 line-clamp-3 flex-grow">
                  {profile.profileSummary || 'No profile summary provided.'}
                </p>

                <div className="mt-auto pt-4 border-t border-gray-100">
                  <button
                    onClick={() => handleUnblockUser(profile)}
                    className="w-full px-6 py-3 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300"
                    style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    Unblock User
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}